/**
 * M7 S32: the Logs screen feature (spec §2.4–§2.6; the career in the Reach only). Since card M3 (dashboard spec §2.2,
 * D-617) the screen is the dashboard's **Log › Case file** section (`registerDashSection`); it has no modal, frame or
 * pause-menu tab of its own.
 *
 * The `logs` key (L, bindings.json) is the section's deep link (`deepLink: 'logs'`): the dashboard's own listener
 * opens it there in flight only (not paused, held (docked: L is the station screen's Launch key) or under another
 * modal) and closes it on a second press (`dashboard.json deepLinks`, on since card M4).
 *
 * It reads the Career from the `m2` service as it is (tabs, leads, questions, Your records and the Case line all
 * work from that alone). The story link (S30) plugs in the rest with `connect`: marking a log read, setting a lead's
 * nav target, template token values, the kept `log` callbacks, the corporation's share and a change signal. Until
 * then a lead answers "no fix" and opened logs stay marked unread.
 */
import { FEATURE_ORDER, type GameContext, type GameFeature } from '../../app/feature'
import { getM2 } from '../../space/world'
import { storyData } from '../../story/data'
import { dashboardOpen, openDashboard, registerDashSection, type DashContext, type DashSection, type DashTarget } from '../../ui/dashboard/registry'
import { createLogsSection, type ClaimSource, type LogsSection, type LogsSectionOptions, type LogsStorySource } from '../../ui/story/logsHost'
import { loadLogsConfig } from '../../ui/story/models/config'
import type { LogsNames } from '../../ui/story/models/records'
import sectionJson from './section.json'

/** After the Charter HUD (530), clear of the wing HUD (535). */
const ORDER = FEATURE_ORDER.charterHud + 3

/** Where L (via the section's deep link) and the debug hooks open the dashboard (Log › Case file). */
export const LOGS_TARGET: DashTarget = Object.freeze({ tab: 'log', section: sectionJson.id })

/** What the story link (S30) adds to the Career-only source. */
export type LogsStoryLink = Partial<Omit<LogsStorySource, 'career' | 'data'>>

export interface LogsScreenFeature extends GameFeature {
  /** The Log › Case file section (null outside a career in the Reach). */
  readonly section: LogsSection | null
  /** Opens the dashboard on Log › Case file (the debug API). */
  openLogs(): void
  /** M7 S39: opens Log › Case file on the Finale (the debug hook); false when no path is on offer. */
  openFinale(): boolean
  /** Plugs the story link's actions and extras in (S30); returns the unplug. */
  connect(link: LogsStoryLink): () => void
  dispose(): void
}

function reachNames(ctx: GameContext): LogsNames {
  const systems = new Map<string, string>()
  const poiSystem = new Map<string, string>()
  for (const s of ctx.reach?.galaxy.systems ?? []) {
    systems.set(s.id, s.name)
    for (const p of s.pois) poiSystem.set(p.id, s.name)
  }
  return {
    system: (id) => systems.get(id) ?? id,
    station: (id) => id,
    hull: (id) => id,
    poi: (id) => poiSystem.get(id) ?? id,
  }
}

/** The dashboard calls the feature makes (default: the registry's; Node tests pass stand-ins). */
export interface LogsDashPort {
  register(s: DashSection): () => void
  open(at: DashTarget): void
  isOpen(): boolean
}

const REGISTRY_PORT: LogsDashPort = {
  register: registerDashSection,
  open: openDashboard,
  isOpen: dashboardOpen,
}

export interface LogsScreenOptions {
  /** The section factory (default `createLogsSection`). */
  section?: (opts: LogsSectionOptions) => LogsSection
  dash?: LogsDashPort
}

export function createLogsScreenFeature(opts: LogsScreenOptions = {}): LogsScreenFeature {
  const makeSection = opts.section ?? createLogsSection
  const dash = opts.dash ?? REGISTRY_PORT
  let section: LogsSection | null = null
  let link: LogsStoryLink = {}
  const offs: (() => void)[] = []

  const feature: LogsScreenFeature = {
    name: 'logsScreen',
    order: ORDER,
    get section(): LogsSection | null {
      return section
    },

    init(ctx: GameContext): void {
      if (ctx.reach === null || ctx.mode !== 'career') return
      const config = loadLogsConfig()
      const base = reachNames(ctx)
      const source: LogsStorySource = {
        career: () => getM2(ctx)?.career() ?? null,
        data: () => storyData(),
        get names(): LogsNames {
          return link.names ?? base
        },
        vars: (id) => link.vars?.(id),
        messages: () => link.messages?.() ?? [],
        corpShare: () => link.corpShare?.() ?? null,
        rows: (id) => link.rows?.(id),
        markRead: (id) => link.markRead?.(id),
        setLeadTarget: (id) => link.setLeadTarget?.(id) ?? false,
        // M7 S34: the claim panel's story side, once the story link is connected.
        get claim(): ClaimSource | undefined {
          return link.claim
        },
        // M7 S39: the finale flow, once the story link is connected.
        get finale(): LogsStorySource['finale'] {
          return link.finale
        },
        onChange: (fn) => link.onChange?.(fn) ?? ((): void => {}),
      }
      const s = makeSection({ source, config })
      section = s
      offs.push(
        dash.register({
          id: sectionJson.id,
          tab: 'log',
          order: sectionJson.order,
          label: sectionJson.label,
          deepLink: 'logs',
          available: (c: DashContext) => c.career,
          component: s.component,
        }),
      )
    },

    openLogs(): void {
      if (section !== null) dash.open(LOGS_TARGET)
    },

    openFinale(): boolean {
      const s = section
      if (s === null) return false
      dash.open(LOGS_TARGET)
      if (!dash.isOpen()) return false
      s.controller.refresh()
      return s.controller.openFinale()
    },

    connect(next: LogsStoryLink): () => void {
      link = next
      return () => {
        if (link === next) link = {}
      }
    },

    dispose(): void {
      for (const off of offs.splice(0)) off()
      section = null
      link = {}
    },
  }
  return feature
}

const feature: LogsScreenFeature = createLogsScreenFeature()

export default feature
